import React, { useState, useEffect, useCallback } from 'react';
import { Search, Gavel } from 'lucide-react';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import Toast, { ToastMessage } from '../common/Toast';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import { Anomaly, AnomalyStatus } from '../file_analytics/types';
import { useCanTriageEvents } from '../file_analytics/permissions';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import AnomalyTriageModal from './AnomalyTriageModal';
import { usePermission } from '../../contexts/PermissionContext';
import '../file_analytics/FileAnalytics.css';

const ACT_EVENTS = 'CMG_EVENTS';

/**
 * Legacy aside-panel row shape. MonitoringNetBody still reads `status==='Open'`
 * to drive its "Open Issues" panel; we keep this exported type compatible and
 * derive open/closed from the live anomaly status (Confirmed/Dismissed = Closed).
 */
export interface Risk {
  id: number;
  rule: string;
  entity: string;
  date: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'Open' | 'Closed';
}

const ALL_STATUSES: AnomalyStatus[] = ['New', 'Under review', 'Confirmed', 'Dismissed'];

/** Open = not yet resolved (anything before a Confirmed/Dismissed disposition). */
const anomalyToRisk = (a: Anomaly): Risk => ({
  id: a.id,
  rule: a.rule_name ?? '',
  entity: a.record_key ?? '',
  date: a.created_on ?? '',
  severity: 'Medium',
  status: a.status === 'Confirmed' || a.status === 'Dismissed' ? 'Closed' : 'Open',
});

interface EventsTabProps {
  onRisksChange?: (risks: Risk[]) => void;
}


const EventsTab: React.FC<EventsTabProps> = ({ onRisksChange }) => {
  const canTriage = useCanTriageEvents();
  const { allowed: canView, loading: viewLoading } = usePermission(ACT_EVENTS, 'view');
  const { formatDateTime } = useEntitySettings();

  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [riskFilter, setRiskFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Triage modal state.
  const [triageTarget, setTriageTarget] = useState<Anomaly | null>(null);

  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, riskFilter, pageSize]);


  const loadAnomalies = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = statusFilter !== 'All' ? { status: statusFilter } : undefined;
      const rows = await FileAnalyticsAPI.listAnomalies(params);
      setAnomalies(rows);
      onRisksChange?.(rows.map(anomalyToRisk));
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to load anomalies' });
      setAnomalies([]);
      onRisksChange?.([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onRisksChange, canView]);

  useEffect(() => { loadAnomalies(); }, [loadAnomalies]);

  // Distinct risk codes from the loaded anomalies (for the risk filter Select).
  const riskCodes = Array.from(
    new Set(anomalies.map(a => a.risk_code).filter((c): c is string => !!c)),
  ).sort();

  const filtered = anomalies.filter(a => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      (a.rule_name ?? '').toLowerCase().includes(term) ||
      (a.risk_code ?? '').toLowerCase().includes(term) ||
      (a.record_key ?? '').toLowerCase().includes(term);
    const matchesRisk = riskFilter === 'All' || a.risk_code === riskFilter;
    return matchesSearch && matchesRisk;
  });

  const totalRecords = filtered.length;
  const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const openTriage = (a: Anomaly) => setTriageTarget(a);

  const filtersActive = !!searchTerm || statusFilter !== 'All' || riskFilter !== 'All';

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to monitoring events." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            value={statusFilter}
            onChange={v => setStatusFilter(String(v ?? 'All'))}
            className="filter-status"
            data-testid="SEL_monitoring_events_status_filter"
            noSearchIcon
            options={[
              { value: 'All', label: 'All Status' },
              ...ALL_STATUSES.map(s => ({ value: s, label: s })),
            ]}
          />
          <FormControl.SearchSelect
            value={riskFilter}
            onChange={v => setRiskFilter(String(v ?? 'All'))}
            className="filter-status"
            data-testid="SEL_monitoring_events_risk_filter"
            noSearchIcon
            options={[
              { value: 'All', label: 'All Risks' },
              ...riskCodes.map(c => ({ value: c, label: c })),
            ]}
          />
          <FormControl.Input
            type="text"
            placeholder="Search anomalies…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_monitoring_events_search"
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Linked risk</th>
                    <th className="table-list-th table-list-cell--status">Triaged status</th>
                    <th className="table-list-th">Assignee</th>
                    <th className="table-list-th">Detected</th>
                    <th className="table-list-th table-list-th--right">Actions</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill', 'pill', '3xs', '3xs', { width: 'icon', align: 'right' }]} />}
                  {!loading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={7} className="table-list-cell">
                        <NoDataFound
                          title={filtersActive ? 'No Matching Anomalies' : 'No Anomalies Found'}
                          message={filtersActive
                            ? 'No anomalies match your filters.'
                            : 'No anomalies detected yet. Run a rule in Data Analytics to detect anomalies.'}
                        />
                      </td>
                    </tr>
                  )}
                  {!loading && paginatedRows.map((a, idx) => (
                    <tr key={a.id} className="table-list-row">
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title">{a.rule_name ?? `Rule #${a.rule_id}`}</span>
                        {a.pipeline_name && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.pipeline_name}</div>
                        )}
                      </td>
                      <td className="table-list-cell">
                        {a.risk_code
                          ? <span className="tstat" title={a.risk_name} data-testid={`anomaly-risk-${a.id}`}>{a.risk_code}</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }} data-testid={`anomaly-risk-${a.id}`}>Unlinked</span>}
                      </td>
                      <td className="table-list-cell">
                        <span
                          style={{ color: 'var(--text-secondary)', fontSize: 12 }}
                          data-testid={`anomaly-status-${a.id}`}
                        >
                          {a.status}
                        </span>
                      </td>
                      <td className="table-list-cell">{a.assignee_name ?? '—'}</td>
                      <td className="table-list-cell" style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {formatDateTime(a.created_on)}
                      </td>
                      <td className="table-list-cell table-list-cell--actions">
                        {canTriage && (
                          <Button
                            variant="secondary-outline"
                            onClick={() => openTriage(a)}
                            data-testid={`BTN_monitoring_anomaly_triage_${a.id}`}
                            title="Triage anomaly"
                          >
                            <Gavel size={14} /> Triage
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="anomalies"
            />
          </div>
        </div>
      </div>

      <AnomalyTriageModal
        isOpen={!!triageTarget}
        anomaly={triageTarget}
        onClose={() => setTriageTarget(null)}
        onUpdated={() => { setTriageTarget(null); loadAnomalies(); }}
      />
    </div>
  );
};

export default EventsTab;
